import React from 'react'

// NOTE: reusable hook to fetch the data from the server
// by fetchHandler function (remember to pass a memoized
// version of fetchHandler to avoid infinite loop),
// it also allows to use it as a local state storage,
// the state can be also mutated with exposed handlers
interface FetchResult<T> {
  meta: unknown
  response: T
}

export const useFetch = <T = unknown>(fetchHandler: () => Promise<FetchResult<T>>) => {
  const [error, setError] = React.useState(null)
  const [meta, setMeta] = React.useState(null)
  const [response, setResponse] = React.useState(null)
  const [isFetching, setIsFetching] = React.useState(true)

  const handleRefetch = React.useCallback(async () => {
    try {
      setIsFetching(true)
      const { meta, response } = await fetchHandler()
      setMeta(meta)
      setResponse(response)
    } catch (err) {
      setError(err)
    } finally {
      setIsFetching(false)
    }
  }, [fetchHandler])

  React.useEffect(() => {
    handleRefetch()
  }, [handleRefetch])

  return {
    state: { error, meta, response, isFetching },
    handlers: {
      handleRefetch,
      handleSetError: setError,
      handleSetMeta: setMeta,
      handleSetResponse: setResponse,
    },
  }
}
